/**
 * Created by neo on 11.01.2026
 * Create Team Modal - Modal for creating a new team for route challenges
 */
import * as React from 'react';
import { useState, useCallback } from 'react';
import { observer } from 'mobx-react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from '@emotion/styled';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { useToast } from '../../../Hooks/useToast';
import { RouteChallengeTeam, createRouteChallengeTeam } from '../../../Model/Engagement/RouteChallenge/RouteChallengeTeam';
import { Input } from '../../../Components/Input';
import { Button } from '../../../Components/Button';
import { Checkbox } from '../../../Components/Checkbox';
import { ImageUploadInput, UploadedImage } from '../../../Components/ImageUploadInput';
import { Theme } from '../../../Theme/Theme';

export type CreateTeamModalProps = {
  isOpen: boolean;
  onClose: () => void;
  challengeId: string;
  onTeamCreated: (team: RouteChallengeTeam) => void;
};

const StyledModal = styled(Modal)`
  .modal-content {
    border-radius: 16px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin-bottom: 8px;
`;

const ErrorText = styled.p`
  font-size: 12px;
  color: ${Theme.Colors.error};
  margin: 4px 0 0 0;
`;

const CheckboxContainer = styled.div`
  margin: 20px 0;
`;

const HelpText = styled.p`
  font-size: 12px;
  color: ${Theme.Colors.warmGrey};
  margin: 4px 0 0 0;
  line-height: 1.4;
`;

export const CreateTeamModal: React.FC<CreateTeamModalProps> = observer(
  ({ isOpen, onClose, challengeId, onTeamCreated }) => {
    const { t } = useTrans();
    const toast = useToast();

    const [teamName, setTeamName] = useState('');
    const [isPrivate, setIsPrivate] = useState(false);
    const [teamImage, setTeamImage] = useState<UploadedImage | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<{ name?: string }>({});

    const handleClose = useCallback(() => {
      if (!isLoading) {
        setTeamName('');
        setIsPrivate(false);
        setTeamImage(null);
        setErrors({});
        onClose();
      }
    }, [isLoading, onClose]);

    const validate = useCallback((): boolean => {
      const newErrors: { name?: string } = {};

      if (!teamName.trim()) {
        newErrors.name = t('team.create.error.nameRequired', { defaultValue: 'Team name is required' });
      } else if (teamName.trim().length < 3) {
        newErrors.name = t('team.create.error.nameTooShort', {
          defaultValue: 'Team name must be at least 3 characters',
        });
      } else if (teamName.trim().length > 50) {
        newErrors.name = t('team.create.error.nameTooLong', {
          defaultValue: 'Team name must be at most 50 characters',
        });
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }, [teamName, t]);

    const handleSubmit = useCallback(async () => {
      if (!validate()) return;

      setIsLoading(true);

      try {
        // Create FormData if image is present
        if (teamImage) {
          const formData = new FormData();
          formData.append('challengeId', challengeId);
          formData.append('name', teamName.trim());
          formData.append('isPrivate', String(isPrivate));
          formData.append('image', teamImage.file);

          const response = await fetch('/coach-program/engagement/route-challenges/teams', {
            method: 'POST',
            body: formData,
            credentials: 'include',
          });

          if (!response.ok) {
            throw new Error('Failed to create team');
          }

          const teamJson = await response.json();
          const team = createRouteChallengeTeam(teamJson);

          toast.showToast(
            t('team.create.success', { defaultValue: 'Team created successfully!' }),
            'success',
          );
          onTeamCreated(team);
          handleClose();
        } else {
          // Create team without image
          const team = createRouteChallengeTeam({
            challengeId,
            name: teamName.trim(),
            isPrivate,
          });

          const createdTeam = await team.save();

          toast.showToast(
            t('team.create.success', { defaultValue: 'Team created successfully!' }),
            'success',
          );
          onTeamCreated(createdTeam);
          handleClose();
        }
      } catch (error) {
        console.error('Error creating team:', error);
        toast.showToast(
          t('team.create.error.failed', { defaultValue: 'Failed to create team. Please try again.' }),
          'error',
        );
      } finally {
        setIsLoading(false);
      }
    }, [validate, teamImage, challengeId, teamName, isPrivate, toast, t, onTeamCreated, handleClose]);

    const handleKeyPress = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !isLoading) {
          handleSubmit();
        }
      },
      [handleSubmit, isLoading],
    );

    return (
      <StyledModal isOpen={isOpen} toggle={handleClose} size="md" centered>
        <ModalHeader toggle={handleClose}>
          {t('team.create.title', { defaultValue: 'Create Team' })}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label htmlFor="teamName">
              {t('team.create.nameLabel', { defaultValue: 'Team Name' })} *
            </Label>
            <Input
              id="teamName"
              type="text"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('team.create.namePlaceholder', { defaultValue: 'Enter team name' })}
              invalid={!!errors.name}
              disabled={isLoading}
            />
            {errors.name && <ErrorText>{errors.name}</ErrorText>}
            <HelpText>
              {t('team.create.nameHint', { defaultValue: '3-50 characters' })}
            </HelpText>
          </FormGroup>

          <FormGroup>
            <ImageUploadInput
              value={teamImage}
              onChange={setTeamImage}
              label={t('team.create.imageLabel', { defaultValue: 'Team Image (Optional)' })}
              maxSizeMB={5}
            />
          </FormGroup>

          <CheckboxContainer>
            <Checkbox
              checked={isPrivate}
              onToggle={() => !isLoading && setIsPrivate(!isPrivate)}
              text={t('team.create.privateLabel', { defaultValue: 'Private Team' })}
            />
            <HelpText>
              {t('team.create.privateHint', {
                defaultValue: 'Private teams require an invite code for others to join',
              })}
            </HelpText>
          </CheckboxContainer>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleClose} disabled={isLoading}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={isLoading}>
            {isLoading
              ? t('team.create.creating', { defaultValue: 'Creating...' })
              : t('team.create.submit', { defaultValue: 'Create Team' })}
          </Button>
        </ModalFooter>
      </StyledModal>
    );
  },
);
